import { lazy, useMemo } from 'react';
import { createAppTheme } from './styles/muiTheme';
import useResolvedThemeMode from './styles/useResolvedThemeMode';
import {
  AuthProvider,
  DatabaseProvider,
  FirebaseAppProvider,
  FirestoreProvider,
  FunctionsProvider,
  useFirebaseApp,
} from 'reactfire';
import firebaseConfig from './helpers/firebaseConfig';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppStateProvider } from './data/AppState/useAppState';
import AuthCheck from './pages/auth/AuthCheck';
import { getFirestore } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { CssBaseline, ThemeProvider } from '@mui/material';
import AppDrawerProvider from './sidebar/AppDrawer/useAppDrawer';
import FarmProvider from './components/FarmProvider/FarmProvider';
import { PollerCacheProvider } from './data/hooks/PollerCacheProvider';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorMsg from './ErrorMsg';
import Sus from './components/Sus/Sus';

import StreamMessageSnackbar from '@components/StreamMessageProvider/StreamMessageSnackbar';

const Home = lazy(() => import('./pages/Home/Home'));
const BrokerEntry = lazy(() => import('./pages/Broker/BrokerEntry'));
const Seeds = lazy(() => import('./pages/Broker/Seeds/Seeds'));
const FeatureFlags = lazy(() => import('./pages/FeatureFlags/FeatureFlags'));
const Automation = lazy(() => import('./pages/mcp/Automation'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const AiTest = lazy(() => import('./pages/AiTest/AiTest'));
const AiTestWeeds = lazy(() => import('./pages/AiTest/AiTestWeeds'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
    },
  },
});

const MainRoutes = () => {

  return (
    <ErrorBoundary FallbackComponent={ErrorMsg}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <FarmProvider>
            <AppDrawerProvider>
              <Sus text='Loading page…'>
                <Routes>
                  <Route path='/' element={<Home />} />
                  <Route path='/broker/:brokerId/*' element={<BrokerEntry />} />
                  <Route path='seeds/' element={<Seeds />} />
                  <Route path='featureflags/' element={<FeatureFlags/>}/>
                  <Route path='mcp/automation' element={<Automation/>}/>
                  <Route path='settings/' element={<Settings/>}/>
                  <Route path='aitest/' element={<AiTest/>}/>
                  <Route path='aitest/weeds' element={<AiTestWeeds/>}/>
                </Routes>
              </Sus>
            </AppDrawerProvider>
          </FarmProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

function ThemedApp() {
  const mode = useResolvedThemeMode();
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthCheck>
        <MainRoutes />
      </AuthCheck>
    </ThemeProvider>
  );
}

function AppRun() {
  const app = useFirebaseApp();
  const database = getDatabase(app);
  const auth = getAuth(app);

  const firestoreInstance = getFirestore(useFirebaseApp());

  return (
    <AuthProvider sdk={auth}>
      <DatabaseProvider sdk={database}>
        <SnackbarProvider Components={{ streamMessage: StreamMessageSnackbar }}>
          <FunctionsProvider sdk={getFunctions(app)}>
            <AppStateProvider>
              <FirestoreProvider sdk={firestoreInstance}>
                <PollerCacheProvider>
                  <ThemedApp />
                </PollerCacheProvider>
              </FirestoreProvider>
            </AppStateProvider>
          </FunctionsProvider>
        </SnackbarProvider>
      </DatabaseProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <FirebaseAppProvider firebaseConfig={firebaseConfig} suspense={true}>
      <AppRun />
    </FirebaseAppProvider>
  );
}
export default App;
